import React from 'react';
import { PHONE_E164 } from '@/config/business';

// ISO date helper
const getCurrentISODate = () => new Date().toISOString().split('T')[0];

export interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath: string; // e.g., "/prices" or "" for homepage
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  dateModified?: string;
  noIndex?: boolean;
}

// Generate BreadcrumbList schema
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Generate WebPage schema with dateModified
export const generateWebPageSchema = (
  name: string, 
  description: string, 
  url: string,
  dateModified?: string
) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": name,
  "description": description,
  "url": url,
  "dateModified": dateModified || getCurrentISODate(),
  "inLanguage": "en-GB",
  "isPartOf": {
    "@type": "WebSite",
    "@id": "https://best-drive.co.uk/#website",
    "name": "Best-Drive Bath",
    "url": "https://best-drive.co.uk"
  },
  "potentialAction": {
    "@type": "ReadAction",
    "target": url
  }
});

// VERIFIED REVIEW DATA - Must match visible content on site
// Last updated: 2026-10-19 | Source: Google Business Profile
export const VERIFIED_REVIEW_DATA = {
  ratingValue: "5.0",
  bestRating: "5",
  worstRating: "1",
  reviewCount: "44",
  ratingSource: "Google"
} as const;

// Enhanced LocalBusiness schema with AggregateRating (attached correctly)
export const generateEnhancedLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "DrivingSchool"],
  "@id": "https://best-drive.co.uk/#localbusiness",
  "name": "Best-Drive Bath",
  "alternateName": "Best-Drive Driving School",
  "url": "https://best-drive.co.uk",
  "logo": "https://best-drive.co.uk/logo.png",
  "image": "https://best-drive.co.uk/og-image.jpg",
  "telephone": PHONE_E164,
  "email": "help@best-drive.co.uk",
  "slogan": "Driving lessons with a clear path — not guesswork",
  "foundingDate": "2001",
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "value": 1
  },
  "priceRange": "£59-£3795",
  "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Bank Transfer"],
  "currenciesAccepted": "GBP",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "07:00",
    "closes": "21:00"
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Bath",
    "addressRegion": "Somerset",
    "postalCode": "BA1",
    "addressCountry": "GB"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 51.3758,
    "longitude": -2.3599
  },
  "areaServed": [
    { "@type": "City", "name": "Bath" },
    { "@type": "City", "name": "Bristol" },
    { "@type": "City", "name": "Keynsham" },
    { "@type": "City", "name": "Saltford" },
    { "@type": "City", "name": "Chippenham" }
  ],
  // AggregateRating intentionally omitted here — single source of truth
  // is the sitewide LocalBusiness JSON-LD in index.html (avoids duplicate
  // aggregateRating flagged by Google Search Console).
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Driving Lesson Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Driving Assessment Lesson",
          "description": "A 2-hour assessment drive in Bath with a DVSA Grade A instructor to identify the right intensive course"
        },
        "price": "118",
        "priceCurrency": "GBP"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Intensive Driving Courses",
          "description": "Structured 5 to 35-hour intensive courses in Bath and Bristol"
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "minPrice": "795",
          "maxPrice": "3795",
          "priceCurrency": "GBP"
        }
      }
    ]
  }
});

// Course schema for intensive courses
export const generateCourseSchema = (
  name: string,
  description: string,
  hours: number,
  price: number,
  days: number
) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": name,
  "description": description,
  "provider": {
    "@type": "DrivingSchool",
    "@id": "https://best-drive.co.uk/#localbusiness",
    "name": "Best-Drive Bath"
  },
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "onsite",
    "duration": `P${days}D`,
    "instructor": {
      "@type": "Person",
      "name": "Ian Perry",
      "jobTitle": "DVSA Grade A Driving Instructor"
    }
  },
  "timeRequired": `PT${hours}H`,
  "offers": {
    "@type": "Offer",
    "price": price,
    "priceCurrency": "GBP",
    "availability": "https://schema.org/InStock",
    "validFrom": "2025-01-01"
  }
});

// HowTo schema generator
export const generateHowToSchema = (
  name: string,
  description: string,
  steps: Array<{ name: string; text: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": name,
  "description": description,
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

// Speakable schema for voice search and Google AI
export const generateSpeakableSchema = (
  url: string,
  cssSelectors: string[]
) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "url": url,
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": cssSelectors
  }
});

// ImageObject schema
export const generateImageSchema = (
  url: string,
  caption: string,
  width: number,
  height: number,
  representativeOfPage?: boolean
) => ({
  "@context": "https://schema.org",
  "@type": "ImageObject",
  "url": url,
  "caption": caption,
  "width": width,
  "height": height,
  ...(representativeOfPage && { "representativeOfPage": true })
});

// Comparison Table schema for AI Overview eligibility
export const generateComparisonTableSchema = (
  name: string,
  description: string,
  items: Array<{ name: string; features: Record<string, string> }>
) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": name,
  "description": description,
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "description": Object.entries(item.features).map(([k, v]) => `${k}: ${v}`).join('; ')
  }))
});

// Helper to get canonical URL (no trailing slash, no www)
export const getCanonicalUrl = (path: string): string => {
  const base = "https://best-drive.co.uk";
  if (!path || path === "/") return base;
  // Remove trailing slash
  const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
  return `${base}${cleanPath}`;
};

// Generate hreflang link element (as JSX) - use hrefLang for React
export const HreflangLink: React.FC<{ path: string }> = ({ path }) => (
  <link rel="alternate" hrefLang="en-gb" href={getCanonicalUrl(path)} />
);

export default {
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateEnhancedLocalBusinessSchema,
  generateCourseSchema,
  generateHowToSchema,
  generateSpeakableSchema,
  generateImageSchema,
  generateComparisonTableSchema,
  getCanonicalUrl
};
